import React, { useState, useEffect } from 'react';

interface DelayedLoaderProps {
  /** Delay in milliseconds before showing the loader (default: 300ms) */
  delay?: number;
  /** The loader component to show after the delay */
  children: React.ReactNode;
}

/**
 * DelayedLoader - Only shows children after a delay threshold
 * 
 * Purpose: Prevents loading screen "flash" for fast operations.
 * If the loading completes before the delay, nothing is shown.
 * If loading takes longer than delay, the loader appears intentionally.
 * 
 * Usage:
 * - Route transitions: Use delay=300ms (most chunks load in 50-200ms)
 * - Auth checks: Use delay=500ms (cold starts can take 1-3s)
 */
const DelayedLoader: React.FC<DelayedLoaderProps> = ({ 
  delay = 300, 
  children 
}) => {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldShow(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  // Don't show anything during the delay period
  // If still loading after delay, show the loader
  return shouldShow ? <>{children}</> : null;
};

export default DelayedLoader;
